import React, { useRef, useState, useCallback } from 'react';
import { Upload } from 'lucide-react';

export interface FileUploadProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSizeMB?: number;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      onFiles,
      accept,
      multiple = true,
      maxSizeMB,
      disabled = false,
      className = '',
      children,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const maxSizeBytes = maxSizeMB != null ? maxSizeMB * 1024 * 1024 : undefined;

    const filterValidFiles = useCallback(
      (files: FileList | File[]): File[] => {
        const arr = Array.from(files);
        if (maxSizeBytes == null) return arr;
        return arr.filter((f) => f.size <= maxSizeBytes);
      },
      [maxSizeBytes],
    );

    const handleFiles = useCallback(
      (files: FileList | File[]) => {
        const valid = filterValidFiles(files);
        if (valid.length > 0) onFiles(valid);
      },
      [filterValidFiles, onFiles],
    );

    const handleClick = () => {
      if (disabled) return;
      inputRef.current?.click();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files?.length) handleFiles(files);
      e.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      setIsDragActive(true);
    };

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      setIsDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDragActive(false);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (disabled) return;
      const files = e.dataTransfer.files;
      if (files?.length) handleFiles(files);
    };

    const containerClasses = [
      'relative rounded-xl border-2 border-dashed transition-all cursor-pointer',
      disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
      !disabled && isDragActive && 'border-brand/40 bg-brand/5',
      !disabled && !isDragActive &&
        'border-subtle bg-surface-primary/50 hover:border-default hover:bg-surface-primary',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const hintParts: string[] = [];
    if (accept) hintParts.push(accept.replace(/,/g, ', '));
    if (maxSizeMB != null) hintParts.push(`max ${maxSizeMB}MB`);

    const defaultContent = (
      <div className="flex flex-col items-center justify-center gap-2 py-10 px-6">
        <Upload size={24} className="text-content-faint" aria-hidden />
        <span className="text-sm text-content-secondary">
          Drop files here or click to upload
        </span>
        {hintParts.length > 0 && (
          <span className="text-2xs text-content-faint">
            {hintParts.join(' • ')}
          </span>
        )}
      </div>
    );

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? undefined : 0}
        aria-disabled={disabled}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={containerClasses}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
        />
        {children ?? defaultContent}
      </div>
    );
  },
);

FileUpload.displayName = 'FileUpload';

export default FileUpload;
